import type { WhatsAppTemplateDto } from "@multi-chat/shared";
import { extractBodyPreview } from "./template-send.js";

/** Two-pane picker from tablet width up; stacked list→detail below. */
export const TEMPLATE_PICKER_SPLIT_MIN_WIDTH_PX = 768;

/** Split picker width from which the phone gets its own column beside the fields. */
export const TEMPLATE_PICKER_THREE_COLUMN_MIN_WIDTH_PX = 820;

export type TemplatePickerLayout = "split" | "stack";
export type TemplatePickerColumns = "two" | "three";

export function templatePickerColumns(pickerWidthPx: number): TemplatePickerColumns {
  return pickerWidthPx >= TEMPLATE_PICKER_THREE_COLUMN_MIN_WIDTH_PX ? "three" : "two";
}
export type TemplatePickerStackView = "list" | "detail";
export type TemplatePickerSurface = "conversation-drawer" | "mobile-sheet";

export function isApprovedSendableTemplate(template: Pick<WhatsAppTemplateDto, "status">): boolean {
  return template.status.trim().toUpperCase() === "APPROVED";
}

export function templatePickerLayout(isSplitViewport: boolean): TemplatePickerLayout {
  return isSplitViewport ? "split" : "stack";
}

/** Desktop/tablet: conversation-scoped bottom drawer. Mobile: portaled bottom Sheet. */
export function templatePickerSurface(isSplitViewport: boolean): TemplatePickerSurface {
  return isSplitViewport ? "conversation-drawer" : "mobile-sheet";
}

/**
 * Desktop picker anchoring: the elements the floating picker is measured against.
 * `details` is the docked Details card while visible, so wide frames get list +
 * fields + phone instead of squeezing into the thread alone.
 */
export interface TemplateDrawerFrame {
  host: HTMLElement;
  thread: HTMLElement;
  details: HTMLElement | null;
}

interface FrameRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface TemplateDrawerGeometry {
  panel: { left: number; top: number; width: number; height: number };
  scrim: { left: number; top: number; width: number; height: number };
}

const DRAWER_INSET_PX = 10;
/** Keeps the conversation header (name, window chip) visible above the picker. */
const DRAWER_HEADER_CLEARANCE_PX = 64;
const DRAWER_MAX_HEIGHT_PX = 560;
const DRAWER_MIN_HEIGHT_PX = 280;

/** Host-relative boxes for the picker panel and its scrim, independent of text direction. */
export function templateDrawerGeometry(input: {
  host: FrameRect;
  thread: FrameRect;
  details: FrameRect | null;
}): TemplateDrawerGeometry {
  const { host, thread, details } = input;
  const start = details ? Math.min(thread.left, details.left) : thread.left;
  const end = details ? Math.max(thread.right, details.right) : thread.right;
  const bottom = thread.bottom - host.top - DRAWER_INSET_PX;
  const top = thread.top - host.top + DRAWER_HEADER_CLEARANCE_PX;
  const height = Math.max(DRAWER_MIN_HEIGHT_PX, Math.min(DRAWER_MAX_HEIGHT_PX, bottom - top));
  return {
    panel: {
      left: start - host.left + DRAWER_INSET_PX,
      top: bottom - height,
      width: Math.max(0, end - start - DRAWER_INSET_PX * 2),
      height,
    },
    scrim: {
      left: start - host.left,
      top: thread.top - host.top,
      width: end - start,
      height: thread.bottom - thread.top,
    },
  };
}

/**
 * First painted pose for the conversation-scoped drawer.
 * Mount in the hidden pose, then commit rest after paint so Motion animates the
 * entrance instead of flashing the resting frame on the first commit.
 */
export function templateDrawerEnterPose(input: {
  reduceMotion: boolean | null;
  enterCommitted: boolean;
}): "resting" | "offscreen" {
  if (input.reduceMotion) return "resting";
  return input.enterCommitted ? "resting" : "offscreen";
}

/**
 * Mobile list/detail. Split layout always shows both panes; stack requires an
 * explicit detail view plus a selected template.
 */
export function templatePickerStackView(input: {
  layout: TemplatePickerLayout;
  requestedView: TemplatePickerStackView;
  selectedId: number | null;
}): TemplatePickerStackView {
  if (input.layout === "split") {
    return "list";
  }
  if (input.selectedId == null) {
    return "list";
  }
  return input.requestedView;
}

const TEMPLATE_PLACEHOLDER_RE = /\{\{\s*[^{}]+?\s*\}\}/g;

/** One-line list snippet: placeholders read as "…" so rows show the message, not syntax. */
export function templatePickerSnippet(text: string | null, maxChars = 88): string | null {
  if (text == null) return null;
  return compactTemplatePreviewText(text.replace(TEMPLATE_PLACEHOLDER_RE, "…"), maxChars);
}

export function compactTemplatePreviewText(
  text: string | null,
  maxChars = 88,
): string | null {
  if (text == null) return null;
  const collapsed = text.replace(/\s+/g, " ").trim();
  if (collapsed.length === 0) return null;
  if (collapsed.length <= maxChars) return collapsed;
  return `${collapsed.slice(0, Math.max(1, maxChars - 1)).trimEnd()}…`;
}

export function templatePickerFilterOptions(
  items: readonly Pick<WhatsAppTemplateDto, "language" | "category">[],
): { languages: string[]; categories: string[] } {
  return {
    languages: uniqueSortedStrings(items.map((item) => item.language)),
    categories: uniqueSortedStrings(items.map((item) => item.category)),
  };
}

export function filterTemplatesForPicker(
  items: readonly WhatsAppTemplateDto[],
  input: {
    query: string;
    language: string | null;
    category: string | null;
  },
): WhatsAppTemplateDto[] {
  const query = input.query.trim().toLowerCase();
  return items.filter((item) => {
    if (input.language !== null && item.language !== input.language) {
      return false;
    }
    if (input.category !== null && item.category !== input.category) {
      return false;
    }
    if (query.length === 0) {
      return true;
    }
    const body = extractBodyPreview(item.components);
    return templateMatchesPickerQuery(
      {
        name: item.name,
        language: item.language,
        category: item.category,
        bodyPreview: body,
      },
      query,
    );
  });
}

export function templateMatchesPickerQuery(
  template: {
    name: string;
    language: string;
    category: string;
    bodyPreview: string | null;
  },
  normalizedQuery: string,
): boolean {
  if (normalizedQuery.length === 0) return true;
  if (template.name.toLowerCase().includes(normalizedQuery)) return true;
  if (template.language.toLowerCase().includes(normalizedQuery)) return true;
  if (template.category.toLowerCase().includes(normalizedQuery)) return true;
  if (template.bodyPreview?.toLowerCase().includes(normalizedQuery)) return true;
  return false;
}

export function templateLanguageDisplayName(language: string, displayLocale: string): string {
  const trimmed = language.trim();
  if (trimmed.length === 0) return language;
  const bcp47 = trimmed.replace(/_/g, "-");
  try {
    const localized = new Intl.DisplayNames([displayLocale], { type: "language" }).of(bcp47);
    if (localized && localized !== bcp47) {
      return localized;
    }
  } catch {
    return trimmed;
  }
  return trimmed;
}

/** Base language only ("en_US" → "English"), for compact badges; filters keep the full name. */
export function templateLanguageShortName(language: string, displayLocale: string): string {
  const base = language.trim().split(/[-_]/)[0] ?? "";
  return templateLanguageDisplayName(base.length > 0 ? base : language, displayLocale);
}

function uniqueSortedStrings(values: readonly string[]): string[] {
  return [...new Set(values)].toSorted((a, b) => a.localeCompare(b));
}
